import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Alert, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { showMessage } from 'react-native-flash-message';
import { collection, doc, getDoc, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import TonyteBackground from '../components/TonyteBackground';
import { auth, firestore } from '../firebase';
import { useProfile } from '../hooks/useProfile';
import { Colors, Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import { LANGUAGE_LOCALES, useI18n } from '../i18n';
import type { RootStackParamList } from '../types';
import { moderatePartnerReview, reviewErrorKey, type ReviewModerationAction } from '../features/partnerReviews/reviewsApi';
import { reviewPalette, StarRow } from '../features/partnerReviews/StarRating';

type Props = NativeStackScreenProps<RootStackParamList, 'AdminReviewReports'>;
type Report = {
  id: string;
  reviewId: string;
  venueId: string;
  reason: string;
  details: string;
  status: 'open' | 'resolved';
  resolution?: string;
  createdAt: number | null;
  snapshot: { rating: number; text: string; reviewerName: string } | null;
};
type ReviewState = 'published' | 'hidden' | 'removed' | 'missing';

const millis = (value: unknown) => (value && typeof (value as { toMillis?: unknown }).toMillis === 'function' ? (value as { toMillis: () => number }).toMillis() : null);

// Admin queue for reported partner reviews (reports_reviews): reports are
// grouped by review; hide / remove / dismiss go through moderatePartnerReviewEu,
// which keeps the partner's rating aggregate exact and closes the reports.
export default function AdminReviewReportsScreen({ navigation }: Props) {
  const { t, language } = useI18n();
  const { theme } = useTheme();
  const palette = reviewPalette(theme);
  const insets = useSafeAreaInsets();
  const { profile } = useProfile(auth.currentUser?.uid || null);
  const allowed = profile?.role === 'admin' || profile?.role === 'co-admin';
  const [scope, setScope] = useState<'open' | 'resolved'>('open');
  const [reports, setReports] = useState<Report[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [reviewStates, setReviewStates] = useState<Record<string, ReviewState>>({});
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!allowed) return undefined;
    setReports(null);
    setFailed(false);
    return onSnapshot(
      query(collection(firestore, 'reports_reviews'), where('status', '==', scope), orderBy('createdAt', 'desc'), limit(100)),
      snap => setReports(snap.docs.map(item => {
        const data = item.data();
        const snapshot = data.reviewSnapshot && typeof data.reviewSnapshot === 'object' ? data.reviewSnapshot : null;
        return {
          id: item.id,
          reviewId: String(data.reviewId || ''),
          venueId: String(data.venueId || ''),
          reason: String(data.reason || 'other'),
          details: String(data.details || ''),
          status: data.status === 'resolved' ? 'resolved' : 'open',
          resolution: data.resolution,
          createdAt: millis(data.createdAt),
          snapshot: snapshot ? { rating: Number(snapshot.rating) || 0, text: String(snapshot.text || ''), reviewerName: String(snapshot.reviewerName || '') } : null,
        };
      })),
      error => {
        console.warn('[Tonyte][AdminReviewReports] load failed', error);
        setFailed(true);
        setReports([]);
      },
    );
  }, [allowed, scope]);

  // One card per reported review, newest report first.
  const groups = useMemo(() => {
    const byReview = new Map<string, Report[]>();
    (reports || []).forEach(report => byReview.set(report.reviewId, [...(byReview.get(report.reviewId) || []), report]));
    return [...byReview.entries()].map(([reviewId, items]) => ({ reviewId, items }));
  }, [reports]);

  useEffect(() => {
    let active = true;
    const missing = groups.map(group => group.reviewId).filter(id => id && !(id in reviewStates));
    if (!missing.length) return undefined;
    Promise.all(missing.map(async id => {
      try {
        const snap = await getDoc(doc(firestore, 'partnerReviews', id));
        return [id, snap.exists() ? (snap.data().status as ReviewState) : 'missing'] as const;
      } catch {
        return [id, 'missing'] as const;
      }
    })).then(entries => {
      if (active) setReviewStates(current => ({ ...current, ...Object.fromEntries(entries) }));
    });
    return () => { active = false; };
  }, [groups, reviewStates]);

  const act = (reviewId: string, action: ReviewModerationAction) => {
    const run = async () => {
      setBusy(reviewId);
      try {
        const result = await moderatePartnerReview(reviewId, action);
        if (result.status) setReviewStates(current => ({ ...current, [reviewId]: result.status as ReviewState }));
        showMessage({ message: t(`partnerReviews.moderated.${action}`), type: 'success' });
      } catch (error) {
        console.warn('[Tonyte][AdminReviewReports] moderation failed', { reviewId, action, error });
        Alert.alert(t('common.error'), t(reviewErrorKey(error)));
      } finally {
        setBusy(null);
      }
    };
    if (action === 'restore') {
      run();
      return;
    }
    Alert.alert(t(`partnerReviews.confirm.${action}Title`), t(`partnerReviews.confirm.${action}Body`), [
      { text: t('common.cancel'), style: 'cancel' },
      { text: t(`partnerReviews.actions.${action}`), style: action === 'dismiss' ? 'default' : 'destructive', onPress: run },
    ]);
  };

  const dateOf = (value: number | null) => (value ? new Date(value).toLocaleDateString(LANGUAGE_LOCALES[language], { day: 'numeric', month: 'short', year: 'numeric' }) : '');

  return (
    <TonyteBackground appearanceAware>
      <View style={[styles.header, { paddingTop: insets.top + 10 }]}>
        <TouchableOpacity style={[styles.iconButton, { backgroundColor: palette.surfaceStrong }]} onPress={() => navigation.goBack()} accessibilityRole="button" accessibilityLabel={t('common.back')}>
          <Icon name="chevron-left" size={20} color={palette.text} />
        </TouchableOpacity>
        <Text style={[styles.title, { color: palette.text }]} accessibilityRole="header">{t('partnerReviews.admin.title')}</Text>
      </View>
      {!allowed ? (
        <Text style={[styles.empty, { color: palette.muted }]}>{t('promotionsAdmin.adminsOnly', 'Admins only.')}</Text>
      ) : (
        <ScrollView contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 30 }]}>
          <View style={styles.scopeRow}>
            {(['open', 'resolved'] as const).map(item => (
              <TouchableOpacity
                key={item}
                onPress={() => setScope(item)}
                style={[styles.chip, { borderColor: scope === item ? Colors.sunset : palette.border, backgroundColor: palette.surface }]}
                accessibilityRole="button"
                accessibilityState={{ selected: scope === item }}
              >
                <Text style={[styles.chipText, { color: scope === item ? Colors.sunset : palette.text }]}>{t(`partnerReviews.admin.${item}`)}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {reports === null ? <ActivityIndicator color={Colors.sunset} style={styles.spinner} />
            : failed ? <Text style={[styles.empty, { color: palette.muted }]}>{t('partnerReviews.admin.loadFailed')}</Text>
              : !groups.length ? <Text style={[styles.empty, { color: palette.muted }]}>{t('partnerReviews.admin.empty')}</Text>
                : groups.map(({ reviewId, items }) => {
                  const latest = items[0];
                  const state = reviewStates[reviewId];
                  const snapshot = items.find(item => item.snapshot)?.snapshot ?? null;
                  return (
                    <View key={reviewId} style={[styles.card, { backgroundColor: palette.surface, borderColor: palette.border }]}>
                      <View style={styles.cardTop}>
                        <Text style={[styles.reviewer, { color: palette.text }]} numberOfLines={1}>{snapshot?.reviewerName || '—'}</Text>
                        {snapshot ? <StarRow value={snapshot.rating} size={12} /> : null}
                      </View>
                      {snapshot?.text ? <Text style={[styles.body, { color: palette.text }]}>{snapshot.text}</Text> : null}
                      <Text style={[styles.meta, { color: palette.faint }]}>
                        {`${latest.venueId} · ${t('partnerReviews.admin.currentStatus')}: ${state ? t(`partnerReviews.admin.status.${state}`) : '…'}`}
                      </Text>
                      {items.map(report => (
                        <View key={report.id} style={[styles.report, { borderTopColor: palette.border }]}>
                          <Text style={[styles.reason, { color: palette.text }]}>
                            {`${t('partnerReviews.admin.reason')}: ${t(`partnerReviews.reasons.${report.reason}`)}`}
                            {report.resolution ? ` · ${t(`partnerReviews.admin.resolution.${report.resolution}`)}` : ''}
                          </Text>
                          {report.details ? <Text style={[styles.meta, { color: palette.muted }]}>{report.details}</Text> : null}
                          <Text style={[styles.meta, { color: palette.faint }]}>{t('partnerReviews.admin.reportedAt').replace('{date}', dateOf(report.createdAt))}</Text>
                        </View>
                      ))}
                      <View style={styles.actions}>
                        {busy === reviewId ? <ActivityIndicator color={Colors.sunset} /> : (
                          <>
                            {state === 'published' ? <ActionButton label={t('partnerReviews.actions.hide')} onPress={() => act(reviewId, 'hide')} danger /> : null}
                            {state === 'hidden' ? <ActionButton label={t('partnerReviews.actions.restore')} onPress={() => act(reviewId, 'restore')} /> : null}
                            {state === 'published' || state === 'hidden' ? <ActionButton label={t('partnerReviews.actions.remove')} onPress={() => act(reviewId, 'remove')} danger /> : null}
                            {scope === 'open' ? <ActionButton label={t('partnerReviews.actions.dismiss')} onPress={() => act(reviewId, 'dismiss')} /> : null}
                            {latest.venueId ? <ActionButton label={t('partnerReviews.admin.openProfile')} onPress={() => navigation.navigate('VenueProfile', { venueId: latest.venueId })} /> : null}
                          </>
                        )}
                      </View>
                    </View>
                  );
                })}
        </ScrollView>
      )}
    </TonyteBackground>
  );
}

function ActionButton({ label, onPress, danger }: { label: string; onPress: () => void; danger?: boolean }) {
  const { theme } = useTheme();
  const palette = reviewPalette(theme);
  return (
    <TouchableOpacity onPress={onPress} style={[styles.action, { borderColor: danger ? 'rgba(248,113,113,0.5)' : palette.border }]} accessibilityRole="button">
      <Text style={[styles.actionText, { color: danger ? '#F87171' : palette.text }]}>{label}</Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingBottom: 10 },
  iconButton: { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center' },
  title: { flex: 1, fontSize: Typography.lg, fontWeight: Typography.extrabold },
  content: { paddingHorizontal: 16, gap: 12 },
  scopeRow: { flexDirection: 'row', gap: 8 },
  chip: { minHeight: 34, paddingHorizontal: 14, borderRadius: 999, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  chipText: { fontSize: 13, fontWeight: Typography.bold },
  spinner: { marginTop: 40 },
  empty: { textAlign: 'center', marginTop: 40, fontSize: 14 },
  card: { borderRadius: 18, borderWidth: 1, padding: 14, gap: 8 },
  cardTop: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  reviewer: { flex: 1, fontSize: 14, fontWeight: Typography.extrabold },
  body: { fontSize: 14, lineHeight: 20 },
  meta: { fontSize: 12, lineHeight: 17 },
  report: { borderTopWidth: StyleSheet.hairlineWidth, paddingTop: 8, gap: 2 },
  reason: { fontSize: 13, fontWeight: Typography.bold },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  action: { minHeight: 36, paddingHorizontal: 12, borderRadius: 12, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  actionText: { fontSize: 13, fontWeight: Typography.bold },
});
